import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, IconButton, Button, TextField, FormControl, InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText,
    List, ListItem, ListItemText, Divider, Paper, CircularProgress, Avatar, Accordion, AccordionSummary, AccordionDetails, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import skillsService from '../services/skills.service';
import QuestionForm from './QuestionForm';

const modalTextFieldStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563EB',
        },
    },
    '& label.Mui-focused': {
        color: '#2563EB',
    },
};

const TestDetailDrawer = ({ test, open, onClose, onTestDeleted, onTestUpdated }) => {
    const [editingTest, setEditingTest] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isQuestionFormOpen, setIsQuestionFormOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const [testResults, setTestResults] = useState([]);
    const [resultsCount, setResultsCount] = useState(null);
    const [loadingResults, setLoadingResults] = useState(false);
    const [showResults, setShowResults] = useState(false);

    useEffect(() => {
        if (test) {
            setEditingTest({ ...test });
            setIsEditMode(false);
            setEditingQuestion(null);
            setIsQuestionFormOpen(false);
            setTestResults([]);
            setResultsCount(null);
            setShowResults(false);
            // Načítaj počet výsledkov hneď pri otvorení draweru
            fetchResultsCount();
        }
    }, [test]);

    const fetchResultsCount = async () => {
        if (!test || !test.id) return;
        try {
            const response = await skillsService.getTestResults(test.id);
            const count = response.data.data?.length || 0;
            setResultsCount(count);
            // Ak už máme dáta, ulož ich
            if (count > 0 && showResults) {
                setTestResults(response.data.data || []);
            }
        } catch (err) {
            console.error("Error fetching test results count", err);
            setResultsCount(0);
        }
    };

    const fetchTestResults = async () => {
        if (!test || !test.id) return;
        // Ak už máme dáta, nepoužívaj request znova
        if (testResults.length > 0) return;
        
        setLoadingResults(true);
        try {
            const response = await skillsService.getTestResults(test.id);
            const results = response.data.data || [];
            setTestResults(results);
            setResultsCount(results.length);
        } catch (err) {
            console.error("Error fetching test results", err);
            setTestResults([]);
            setResultsCount(0);
        } finally {
            setLoadingResults(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        if (nameParts.length > 1) {
            return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
        } else if (nameParts[0] && nameParts[0].length > 1) {
            return nameParts[0].substring(0, 2).toUpperCase();
        }
        return (name[0] || '').toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('sk-SK', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getScoreDisplay = (result) => {
        if (test.test_type === 'technical') {
            if (result.score !== null && result.total !== null) {
                const percentage = Math.round((result.score / result.total) * 100);
                return `${result.score}/${result.total} (${percentage}%)`;
            }
            return 'N/A';
        } else {
            // Pre soft skills sa výsledky zobrazujú podľa kategórií v samostatných Chip-och
            return 'N/A';
        }
    };

    const handleSaveTest = async () => {
        if (!editingTest.name.trim()) {
            setError('Test name is required.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const updateData = {
                name: editingTest.name.trim(),
                description: editingTest.description || null,
                test_type: editingTest.test_type,
                evaluates: editingTest.test_type === 'soft_skills' ? editingTest.evaluates : null,
                time_per_question: editingTest.time_per_question ? parseInt(editingTest.time_per_question, 10) : null,
                is_active: editingTest.is_active,
                questions: editingTest.questions || []
            };

            const response = await skillsService.updateTest(test.id, updateData);
            // Update local state with fresh data from server
            if (response.data) {
                setEditingTest(response.data);
            }
            setIsEditMode(false);
            // Don't call onTestUpdated here - it causes useEffect to reset editingTest
            // We already have the fresh data from response.data
        } catch (err) {
            setError('Failed to update test. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTest = async () => {
        setDeleting(true);
        try {
            if (onTestDeleted) {
                await onTestDeleted(test.id);
            }
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error("Error in delete handler:", error);
        } finally {
            setDeleting(false);
        }
    };

    const handleAddQuestion = () => {
        setEditingQuestion(null);
        setIsQuestionFormOpen(true);
    };

    const handleEditQuestion = (question) => {
        setEditingQuestion(question);
        setIsQuestionFormOpen(true);
    };

    const handleSaveQuestion = async (questionData) => {
        const updatedQuestions = [...(editingTest.questions || [])];
        
        if (questionData.id) {
            // Update existing question
            const index = updatedQuestions.findIndex(q => q.id === questionData.id);
            if (index !== -1) {
                updatedQuestions[index] = { ...updatedQuestions[index], ...questionData };
            }
        } else {
            // Add new question
            updatedQuestions.push(questionData);
        }

        setEditingTest({ ...editingTest, questions: updatedQuestions });
        setIsQuestionFormOpen(false);
        setEditingQuestion(null);
    };

    const handleDeleteQuestion = (questionId) => {
        const updatedQuestions = (editingTest.questions || []).filter(q => q.id !== questionId);
        setEditingTest({ ...editingTest, questions: updatedQuestions });
    };

    const handleCancelQuestion = () => {
        setIsQuestionFormOpen(false);
        setEditingQuestion(null);
    };

    const handleClose = () => {
        onClose();
        if (onTestUpdated) {
            onTestUpdated();
        }
    };

    if (!test || !editingTest) {
        return null;
    }

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                keepMounted={false}
                disableRestoreFocus={true}
            >
                <Box sx={{ width: { xs: '100vw', sm: 600 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={handleClose}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {isEditMode ? 'Edit Test' : test.name}
                            </Typography>
                        </Box>
                        {!isEditMode && (
                            <Box>
                                <IconButton onClick={() => setIsEditMode(true)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => setDeleteDialogOpen(true)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {isEditMode ? (
                        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                            <TextField
                                label="Test Name"
                                variant="outlined"
                                fullWidth
                                value={editingTest.name}
                                onChange={(e) => setEditingTest({ ...editingTest, name: e.target.value })}
                                required
                                sx={{ ...modalTextFieldStyle, mb: 2 }}
                            />
                            <TextField
                                label="Description"
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={3}
                                value={editingTest.description || ''}
                                onChange={(e) => setEditingTest({ ...editingTest, description: e.target.value })}
                                sx={{ ...modalTextFieldStyle, mb: 2 }}
                            />
                            <TextField
                                label="Time per Question (seconds)"
                                variant="outlined"
                                fullWidth
                                type="number"
                                value={editingTest.time_per_question || ''}
                                onChange={(e) => setEditingTest({ ...editingTest, time_per_question: e.target.value })}
                                sx={{ ...modalTextFieldStyle, mb: 2 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editingTest.is_active}
                                        onChange={(e) => setEditingTest({ ...editingTest, is_active: e.target.checked })}
                                        sx={{
                                            color: '#2563EB',
                                            '&.Mui-checked': {
                                                color: '#2563EB',
                                            },
                                        }}
                                    />
                                }
                                label="Active"
                                sx={{ mb: 2 }}
                            />
                            {error && (
                                <Typography color="error" sx={{ mb: 2 }}>
                                    {error}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                <Button onClick={() => { setIsEditMode(false); setEditingTest({ ...test }); }} sx={{ color: 'grey.700', borderRadius: '12px' }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSaveTest}
                                    variant="contained"
                                    disabled={loading}
                                    sx={{
                                        bgcolor: '#2563EB',
                                        color: 'white',
                                        borderRadius: '12px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            bgcolor: '#1D4ED8',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {test.description || 'No description'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Chip
                                        label={test.test_type === 'technical' ? 'Technical' : 'Soft Skills'}
                                        color={test.test_type === 'technical' ? 'primary' : 'secondary'}
                                        size="small"
                                    />
                                    <Chip
                                        label={test.is_active ? 'Active' : 'Inactive'}
                                        color={test.is_active ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Box>
                                {test.time_per_question && test.questions && (
                                    <Typography variant="body2" color="text.secondary">
                                        Estimated time: {Math.floor((test.time_per_question * test.questions.length) / 60)}m {((test.time_per_question * test.questions.length) % 60)}s
                                    </Typography>
                                )}
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Accordion 
                                expanded={showResults} 
                                onChange={(event, isExpanded) => {
                                    setShowResults(isExpanded);
                                    if (isExpanded) {
                                        fetchTestResults();
                                    }
                                }}
                                sx={{ mb: 2 }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">
                                        Employee Results ({resultsCount !== null ? resultsCount : testResults.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {loadingResults ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : testResults.length === 0 ? (
                                        <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                                            No employees have completed this test yet.
                                        </Typography>
                                    ) : (
                                        <Box>
                                            {testResults.map((result, index) => (
                                                <Paper key={result.id} sx={{ p: 2, mb: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                                                        <Avatar sx={{ bgcolor: '#2563EB', width: 48, height: 48 }}>
                                                            {getInitials(result.employee_name)}
                                                        </Avatar>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                {result.employee_name}
                                                            </Typography>
                                                            {result.employee_position && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                    {result.employee_position}
                                                                </Typography>
                                                            )}
                                                            {test.test_type === 'technical' ? (
                                                                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                                                    <Chip 
                                                                        label={`Score: ${getScoreDisplay(result)}`}
                                                                        size="small"
                                                                        sx={{ bgcolor: '#2563EB', color: 'white' }}
                                                                    />
                                                                    {result.badge_level && (
                                                                        <Chip 
                                                                            label={`Badge: ${result.badge_level}`}
                                                                            size="small"
                                                                            color="secondary"
                                                                        />
                                                                    )}
                                                                    <Chip 
                                                                        label={`Completed: ${formatDate(result.completed_at)}`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </Stack>
                                                            ) : (
                                                                <Box sx={{ mb: 1 }}>
                                                                    {result.answers && result.answers.length > 0 && (() => {
                                                                        const categoryScores = {};
                                                                        result.answers.forEach(answer => {
                                                                            if (answer.category && answer.score !== null && answer.score !== undefined) {
                                                                                if (!categoryScores[answer.category]) {
                                                                                    categoryScores[answer.category] = [];
                                                                                }
                                                                                categoryScores[answer.category].push(answer.score);
                                                                            }
                                                                        });
                                                                        
                                                                        const categoryAverages = Object.keys(categoryScores).map(category => {
                                                                            const scores = categoryScores[category];
                                                                            const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
                                                                            return { category, average: avg };
                                                                        });
                                                                        
                                                                        return (
                                                                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                                                {categoryAverages.map(({ category, average }) => (
                                                                                    <Chip 
                                                                                        key={category}
                                                                                        label={`${category}: ${average.toFixed(1)}/5`}
                                                                                        size="small"
                                                                                        sx={{ bgcolor: '#2563EB', color: 'white' }}
                                                                                    />
                                                                                ))}
                                                                                {result.badge_level && (
                                                                                    <Chip 
                                                                                        label={`Badge: ${result.badge_level}`}
                                                                                        size="small"
                                                                                        color="secondary"
                                                                                    />
                                                                                )}
                                                                                <Chip 
                                                                                    label={`Completed: ${formatDate(result.completed_at)}`}
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                />
                                                                            </Stack>
                                                                        );
                                                                    })()}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Questions ({editingTest.questions?.length || 0})</Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddQuestion}
                                    variant="contained"
                                    sx={{
                                        bgcolor: '#2563EB',
                                        color: 'white',
                                        '&:hover': { bgcolor: '#1D4ED8' }
                                    }}
                                >
                                    Add Question
                                </Button>
                            </Box>

                            {isQuestionFormOpen && (
                                <Box sx={{ mb: 2 }}>
                                    <QuestionForm
                                        question={editingQuestion}
                                        testType={test.test_type}
                                        evaluates={test.evaluates || []}
                                        onSave={handleSaveQuestion}
                                        onCancel={handleCancelQuestion}
                                    />
                                </Box>
                            )}

                            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                                {editingTest.questions && editingTest.questions.length > 0 ? (
                                    <List>
                                        {editingTest.questions.map((question, index) => (
                                            <React.Fragment key={question.id || index}>
                                                <Paper sx={{ p: 2, mb: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                {index + 1}. {question.question}
                                                            </Typography>
                                                            {test.test_type === 'soft_skills' && (
                                                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                                    {question.category && (
                                                                        <Chip label={question.category} size="small" />
                                                                    )}
                                                                    {question.is_reverse_scored && (
                                                                        <Chip 
                                                                            label="Reverse" 
                                                                            size="small" 
                                                                            color="warning"
                                                                            sx={{ bgcolor: '#ff9800', color: 'white' }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            )}
                                                            {test.test_type === 'technical' && question.options && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    {question.options.map((opt, optIndex) => (
                                                                        <Typography key={optIndex} variant="body2" sx={{ ml: 2 }}>
                                                                            {opt.label}: {opt.text} {opt.label === question.correct_answer && '✓'}
                                                                        </Typography>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            <IconButton onClick={() => handleEditQuestion(question)} size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleDeleteQuestion(question.id)} size="small" color="error">
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                                {index < editingTest.questions.length - 1 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                                        No questions yet. Add your first question!
                                    </Typography>
                                )}
                            </Box>

                            {editingTest.questions && editingTest.questions.length > 0 && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSaveTest}
                                        disabled={loading}
                                        sx={{
                                            bgcolor: '#2563EB',
                                            color: 'white',
                                            '&:hover': { bgcolor: '#1D4ED8' }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Save All Changes'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Drawer>

            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                keepMounted={false}
                disableRestoreFocus={true}
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                    {"Are you sure you want to delete this test?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This action is permanent and cannot be undone. All test results, badges, and questions will be deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ color: 'grey.700', borderRadius: '12px' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteTest} autoFocus color="error" variant="contained" disabled={deleting} sx={{ borderRadius: '12px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                        {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TestDetailDrawer;




